import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "otc_auth_token";

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 60000,
});

// Request interceptor: add Bearer token from localStorage as fallback for cookie issues
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Store token after successful login
export const storeToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
};

export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// Articles
export const fetchArticles = async (params = {}) => {
  const { data } = await api.get("/articles", { params });
  return data;
};

export const fetchScheduledArticles = async () => {
  const { data } = await api.get("/articles/scheduled");
  return data;
};

export const fetchArticle = async (id) => {
  const { data } = await api.get(`/articles/${id}`);
  return data;
};

export const createArticle = async (formData) => {
  const { data } = await api.post("/articles", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

export const updateArticle = async (id, formData) => {
  const { data } = await api.put(`/articles/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

export const deleteArticle = async (id) => {
  const { data } = await api.delete(`/articles/${id}`);
  return data;
};

// Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  // Return full URL
  return `${BACKEND_URL}${data.url}`;
};

// Categories & Authors
export const fetchCategories = async () => {
  const { data } = await api.get("/categories");
  return data;
};

export const fetchActiveCategories = async () => {
  const { data } = await api.get("/categories/active");
  return data;
};

export const createCategory = async ({ name, color }) => {
  const { data } = await api.post("/categories", { name, color });
  return data;
};

export const updateCategory = async (id, { name, color }) => {
  const { data } = await api.put(`/categories/${id}`, { name, color });
  return data;
};

export const deleteCategory = async (id) => {
  const { data } = await api.delete(`/categories/${id}`);
  return data;
};

export const fetchAuthors = async () => {
  const { data } = await api.get("/authors");
  return data;
};

// Contributors
export const fetchContributors = async () => {
  const { data } = await api.get("/contributors");
  return data;
};

export const createContributor = async (formData) => {
  const { data } = await api.post("/contributors", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const updateContributor = async (id, formData) => {
  const { data } = await api.put(`/contributors/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const deleteContributor = async (id) => {
  const { data } = await api.delete(`/contributors/${id}`);
  return data;
};

// Seed
export const seedDatabase = async () => {
  const { data } = await api.post("/seed");
  return data;
};

export const forceSeedDatabase = async () => {
  const { data } = await api.post("/seed/force");
  return data;
};

// Helper to resolve image URL
export const resolveImageUrl = (image) => {
  if (!image) return "";
  if (image.startsWith("http")) return image;
  return `${BACKEND_URL}${image}`;
};

// Live Scores
export const fetchScores = async (league) => {
  const params = league ? { league } : {};
  const { data } = await api.get("/scores", { params });
  return data;
};

// Trending articles by views
export const fetchTrendingArticles = async (limit = 5) => {
  const { data } = await api.get("/articles-trending", { params: { limit } });
  return data;
};

// On This Day in Sport
export const fetchOnThisDay = async () => {
  const { data } = await api.get("/on-this-day");
  return data;
};

// Article Statistics (admin)
export const fetchArticleStats = async () => {
  const { data } = await api.get("/articles/stats");
  return data;
};

// Search articles
export const searchArticles = async (q, limit = 20) => {
  const { data } = await api.get("/articles/search", { params: { q, limit } });
  return data;
};

export default api;
